import type { Meta, Video } from './index.ts';

/** A schedule describes a channel; its programme IDs are never playback IDs. */
export function isLiveChannel(meta: Pick<Meta, 'type' | 'behaviorHints'>): boolean {
  // Cinemeta also uses hasScheduledVideos for ordinary upcoming episodes.
  // A schedule hint alone must not replace their video IDs with a channel ID.
  return meta.type === 'tv' || meta.behaviorHints?.isLive === true;
}
export function playbackVideoId(meta: Meta, requested?: string): string | undefined {
  return isLiveChannel(meta) ? meta.id : requested;
}
export function utcGuideDay(value: unknown): string | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return;
  const time = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(time) && new Date(time).toISOString().slice(0, 10) === value ? value : undefined;
}
export type GuideWindow = { day: string; from: number; to: number; utcDays: string[] };
export function localGuideDay(now = Date.now()): string {
  const date = new Date(now);
  return `${String(date.getFullYear()).padStart(4, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
/** Calendar midnights, rather than a fixed 24 hours, preserve DST transitions. */
export function localGuideWindow(day: string): GuideWindow | undefined {
  if (!utcGuideDay(day)) return;
  const start = new Date(`${day}T00:00:00`);
  if (localGuideDay(start.getTime()) !== day) return; // A skipped local date.
  const end = new Date(start); end.setDate(end.getDate() + 1);
  const from = start.getTime(), to = end.getTime(), utcDays: string[] = [];
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from || to - from > 172_800_000) return;
  for (let at = Math.floor(from / 86_400_000) * 86_400_000; at < to; at += 86_400_000) {
    const date = new Date(at).toISOString().slice(0, 10);
    if (!utcGuideDay(date)) return;
    utcDays.push(date);
  }
  return { day, from, to, utcDays };
}
/** Require an explicit timezone and a real calendar date; Date.parse alone
 * accepts normalized February 30 and locale-dependent timezone-less strings. */
export function programmeTime(value: unknown): number | undefined {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}T(?:[01]\d|2[0-3]):[0-5]\d:[0-5]\d(?:\.\d{1,3})?(?:Z|[+-](?:[01]\d|2[0-3]):[0-5]\d)$/.test(value) || !utcGuideDay(value.slice(0, 10))) return;
  const time = Date.parse(value);
  return Number.isFinite(time) ? time : undefined;
}
export type Programme = { video: Video; start: number; end: number; status: 'past' | 'now' | 'upcoming' };
/** Half-open intervals keep midnight and consecutive programmes distinct.
 * Conflicting duplicate IDs are omitted instead of choosing a made-up schedule. */
export function channelProgrammes(meta: Meta, day: string | GuideWindow, now = Date.now()): Programme[] {
  if (!isLiveChannel(meta) || !Number.isFinite(now)) return [];
  const from = typeof day === 'string' ? utcGuideDay(day) && Date.parse(`${day}T00:00:00.000Z`) : day.from;
  const to = typeof day === 'string' ? Number(from) + 86_400_000 : day.to;
  if (typeof from !== 'number' || !Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const rows = new Map<string, Programme>(), conflicts = new Set<string>();
  for (const video of (meta.videos || []).slice(0, 8000)) {
    const start = programmeTime(video.startTime), end = programmeTime(video.endTime);
    if (!video.id || start === undefined || end === undefined || end <= start) continue;
    const prior = rows.get(video.id);
    if (prior && (prior.start !== start || prior.end !== end || prior.video.title !== video.title)) conflicts.add(video.id);
    if (!prior) rows.set(video.id, { video, start, end, status: now < start ? 'upcoming' : now >= end ? 'past' : 'now' });
  }
  return [...rows.values()].filter(p => !conflicts.has(p.video.id) && p.end > from && p.start < to)
    .sort((a, b) => a.start - b.start || a.end - b.end || a.video.id.localeCompare(b.video.id));
}

/** Expired/empty schedules and failed metadata reads retry without tight polling. */
export function guideRefreshDelay(meta: Meta, now = Date.now(), failed = false): number {
  const future = (meta.videos || []).some(video => {
    const start = programmeTime(video.startTime), end = programmeTime(video.endTime);
    return start !== undefined && end !== undefined && end > start && end > now;
  });
  return failed || !future ? 60_000 : 900_000;
}
